//@flow
import React, {useEffect, useRef} from 'react';
import {useFormContext} from 'react-hook-form';
import {Typography, makeStyles} from '@material-ui/core';

import CustomButton from '../../../../../../elements/CustomButton/CustomButton';
import FormField from '../../FormField';
import SegmentForm from './SegmentForm';
import {
  BLOG_FORM_FIELDS,
  getDefaultContentSegment,
} from '../../../../constants';
import {colors} from '../../../../../../assets/styles/colors';
import {getImageUrl} from '../../../../../../utils/image_provider';
import {
  useCurrentBlogPost,
  useBlogFormDraftValue,
} from '../../../../utils/blog-post';
import {useDebounceEffect} from '../../../../../../utils/react';

const useClasses = makeStyles(theme => ({
  formSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '1rem',
    borderRadius: '.75rem',
    background: 'white',
  },
  segmentFormContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.75rem',
    borderRadius: '.75rem',
    padding: '1rem',
    background: colors.grey200,

    '&[data-error="true"]': {
      background: `${colors.error}1A`,
    },
  },
  addButton: {
    borderRadius: '.75rem',
  },
}));

export default function ContentSection() {
  const classes = useClasses();

  const currentBlogPost = useCurrentBlogPost();

  const {draft, updateDraft} = useBlogFormDraftValue();

  const {
    register,
    setValue,
    getValues,
    watch,
    errors,
    clearErrors,
  } = useFormContext();

  const draftAppliedRef = useRef(false);

  useEffect(() => {
    register(BLOG_FORM_FIELDS.content);
  }, [register]);

  useEffect(() => {
    if (!currentBlogPost) {
      setValue(BLOG_FORM_FIELDS.content, [getDefaultContentSegment()]);
      return;
    }

    const content = currentBlogPost.sections.map(section => ({
      subtitle: section.title,
      text: section.content,
      images: [
        {
          image: section.image1Id,
          alt: section.image1Description,
          preview: getImageUrl(section.image1Id),
        },
        {
          image: section.image2Id,
          alt: section.image2Description,
          preview: getImageUrl(section.image2Id),
        },
      ],
    }));

    setValue(BLOG_FORM_FIELDS.content, content);

    return () => {
      setValue(BLOG_FORM_FIELDS.content, []);
    };
  }, [currentBlogPost, setValue]);

  useEffect(() => {
    if (draftAppliedRef.current) return;
    if (currentBlogPost) return;
    if (!draft) return;
    if (!draft.content) return;

    setValue(
      BLOG_FORM_FIELDS.content,
      draft.content?.map(item => {
        return {
          ...getDefaultContentSegment(),
          subtitle: item.subtitle,
          text: item.text,
        };
      })
    );

    draftAppliedRef.current = true;
  }, [currentBlogPost, draft, setValue]);

  const content = watch(BLOG_FORM_FIELDS.content, []);
  const contentError = errors[BLOG_FORM_FIELDS.content];

  useDebounceEffect(
    () => {
      updateDraft({
        [BLOG_FORM_FIELDS.content]: content?.map(item => {
          return {
            ...getDefaultContentSegment(),
            subtitle: item.subtitle,
            text: item.text,
          };
        }),
      });
    },
    1000,
    [content]
  );

  const handleAddNewSegment = () => {
    const previousContent = getValues(BLOG_FORM_FIELDS.content);
    setValue(BLOG_FORM_FIELDS.content, [
      ...previousContent,
      getDefaultContentSegment(),
    ]);
  };

  const handleRemove = index => {
    const previousContent = getValues(BLOG_FORM_FIELDS.content);
    setValue(
      BLOG_FORM_FIELDS.content,
      previousContent.filter((_, i) => i !== index)
    );
  };

  return (
    <section className={classes.formSection}>
      <FormField
        title="6. Crear contenido"
        subtitle="Agrega segmentos y personaliza "
      >
        <div
          className={classes.segmentFormContainer}
          data-error={!!contentError}
        >
          {contentError && (
            <Typography color="error" variant="subtitle2">
              {contentError.message}
            </Typography>
          )}
          {content.map((segment, index) => (
            <SegmentForm
              key={segment.id}
              index={index}
              onDelete={() => handleRemove(index)}
              subtitle={segment.subtitle}
              text={segment.text}
              images={segment.images}
              onChange={content => {
                const previousContent = getValues(BLOG_FORM_FIELDS.content);

                const newContent = previousContent.map((segment, i) => {
                  if (i !== index) return segment;
                  return content;
                });

                setValue(BLOG_FORM_FIELDS.content, newContent);
                clearErrors(BLOG_FORM_FIELDS.content);
              }}
            />
          ))}
          <CustomButton
            variant="outlined"
            fullWidth
            className={classes.addButton}
            onClick={handleAddNewSegment}
          >
            + Añadir segmento
          </CustomButton>
        </div>
      </FormField>
    </section>
  );
}
